import { Link } from 'react-router-dom'
import type { ProvisionalNet } from '../api/public'
import { withToPar } from './toPar'
import { usePlayerPaths } from './playerPaths'

/**
 * The provisional low net, with each player's completed hole count (FR-035d).
 *
 * # It is deliberately not a ranking
 *
 * Whoever has played FEWEST holes has the lowest total, because they have
 * simply added up less golf — a player four holes into a nine "leads" a field
 * that has played seven. So this states the hole count beside every total and
 * leaves the reader to judge, which a person can do at a glance and a table
 * sorted by net cannot.
 *
 * The projection is where a single low net is actually named, and it does it
 * honestly by restricting the field to the holes everybody has played. This
 * links there rather than repeating the claim.
 *
 * # Its columns match the committed table on purpose
 *
 * Handicap · Gross · Net, each as "12 (+1)", exactly as `Placings` renders a
 * week that has been scored -- so a reader does not have to learn a second
 * layout when a week moves from being played to being final. Commissioner,
 * 2026-11-03: *"the view on the complete view is how I want to update the in
 * progress view."*
 *
 * **The one column it cannot carry is Points.** The low net is not determined
 * until every player has finished (FR-062), so a points column here would show
 * nought awarded, which reads as a result rather than as an absence. The
 * to-par column it replaced said the same thing as the figure in brackets.
 */
export function NetSoFar({ rows, eventId }: { rows: ProvisionalNet[]; eventId: number }) {
  const { pathFor } = usePlayerPaths()
  if (rows.length === 0) return null
  // A player with no hole sets no spread: they have no score, and may not be
  // playing at all (FR-036k).
  const holes = rows.map((p) => p.holes_completed).filter((n) => n > 0)
  const uneven = holes.length > 0 && Math.min(...holes) !== Math.max(...holes)

  return (
    <section className="low-net" data-testid="provisional-low-net">
      <h3>Net so far</h3>
      <p className="note note-provisional">
        Provisional. Ordered by net against par for the holes each player has finished, so someone a
        few holes in is not shown leading on a smaller total. The low net is not awarded until
        everybody has finished (FR-062).
      </p>
      <div className="scroll-x">
        <table className="table low-net-table">
          <thead>
            <tr>
              <th scope="col">Player</th>
              <th scope="col" className="numeric" title="Strokes received on the holes played">
                Handicap
              </th>
              <th scope="col" className="numeric">
                Gross
              </th>
              <th scope="col" className="numeric">
                Net
              </th>
              <th scope="col" className="numeric">
                Through
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.player_id} data-testid={`net-${p.player_id}`}>
                <th scope="row">
                  <Link to={pathFor(p.player_id)}>{p.display_name}</Link>
                </th>
                <td className="numeric">
                  {p.playing_handicap ?? '—'}
                  {p.playing_handicap_overridden && (
                    <abbr
                      className="mark"
                      title="Set by the commissioner, not from the handicap index"
                    >
                      *
                    </abbr>
                  )}
                </td>
                {/* Decimal STRINGS, shown exactly as they arrived. Parsing one
                    to a number would reintroduce in the browser the error the
                    scoring core exists to avoid -- `withToPar` works on the
                    string. */}
                <td className="numeric">{withToPar(p.gross, p.gross_to_par)}</td>
                <td className="numeric strong" data-testid={`net-to-par-${p.player_id}`}>
                  {p.holes_completed === 0 ? '—' : withToPar(p.net_so_far, p.net_to_par)}
                </td>
                <td className="numeric">{p.holes_completed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {uneven && (
        <p className="note">
          The field is spread across {Math.min(...holes)}–{Math.max(...holes)} holes. The order is
          against par, which allows for that;{' '}
          <Link to={`/events/${eventId}/projection`}>the projection</Link> compares only the holes
          everybody has played.
        </p>
      )}
    </section>
  )
}
